/**
 * RegisterPaymentPage (billing) — Registrar Pagamento (POST /billing/payments).
 *
 * 3 etapas:
 *  1) Dados do Recebimento — tudo do payload fora de invoices[]: data (+hora opcional), valor,
 *     tipo de pagamento e campos dependentes, fonte/sub-rogação/aval a devolver, pagador com
 *     busca no person service e comprovante [placeholder]. O responsável não é campo de tela:
 *     é o usuário logado. Os blocos de tipo de pagamento e de pagador são compartilhados com a
 *     baixa/liquidação de título (components/molecules/payment).
 *  2) Selecionar Contratos — busca no contracts-management + seleção de operações.
 *  3) Selecionar Faturas — invoices OPEN das operações; por fatura: value/total_payment/
 *     cancel_invoice/uncollect. Barra de alocação até 100% do valor informado (submete só em 100%).
 *
 * Comprovante: o PDF sobe antes do envio e o nome canônico volta do backend em file_name_receipt.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    FaChevronDown, FaChevronUp, FaCheckSquare, FaRegSquare,
    FaCloudUploadAlt, FaTimes,
    FaPen, FaExclamationTriangle,
} from 'react-icons/fa';
import PaymentTypeFields, { PaymentTypeValue, emptyPaymentTypeValue, assetDationTotal } from '../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer, onlyDigits } from '../../molecules/payment/PayerFields';
import { buildPaymentBlock, newPaymentId } from '../../molecules/payment/paymentPayload';
import PaymentDateInput, { toIsoInstant } from '../../molecules/payment/PaymentDateInput';
import { amountHint } from '../../molecules/payment/paymentHints';
import HintBadge from '../../molecules/payment/HintBadge';
import { PaymentType, PaymentMethod } from '../../../constants/payment';
import { AuthContext } from '../../../services/Auth/AuthContext';
import {
    listOpenChargesByOperation, processPayment, markInvoices, listInvoicesByPayment,
    PaymentInvoiceItem,
    uploadPaymentReceipt, listPaymentSources,
    ProcessPaymentPayload, MarkInvoiceItem, PaymentActionPromise, PaymentRow,
} from './services/billingApi';
import {
    PaymentSourceOption, defaultPaymentSource, allowsGuarantorRefund,
} from './services/paymentSources';
import PendingPaymentPicker from '../../molecules/payment/PendingPaymentPicker';
import PaymentInfoModal from './modals/PaymentInfoModal';
import BulkInvoiceActionsModal, { BulkAction } from './modals/BulkInvoiceActionsModal';
import { FilterBuilder, AppliedFilter, serializeFilters } from '../../molecules/FilterBuilder';
import { operationsFilterSchema } from '../operations/constants/operationFilterSchema';
import { listOperations, SettlementOperation } from '../settlement/services/settlementApi';
import { formatTaxId } from '../../../utils/taxIdUtils';
import {
    LiquidationMode, MODE_TO_TOTAL_PAYMENT, MODE_LABELS, MODE_ACTIONS, WATERFALL_HINT,
    PAYMENT_MODES_BY_SCENARIO, modeOf, clampMode,
} from './constants/liquidationModes';
import { CHARGE_SCENARIO_LABELS } from './constants/chargeFilterSchema';
import PageForm from '../../templates/PageForm';
import CurrencyInput from '../../molecules/CurrencyInput';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';

/**
 * Os dois atos que esta tela sabe fazer.
 *
 * `REGISTER` é o ato ÚNICO: registra o dinheiro, escolhe o que ele paga e liquida na mesma
 * submissão. Está **deprecated** — sem conferência no meio — e não é mais oferecido no menu.
 *
 * `IDENTIFY` é o passo 2 do ato desmembrado: o dinheiro já entrou (passo 1, financeiro) e aqui a
 * cobrança apenas diz QUAIS faturas ele paga. A liquidação vem na aprovação (passo 3).
 *
 * Um componente e não dois arquivos: as etapas 2 e 3 são idênticas nos dois atos, e duplicá-las
 * faria uma correção feita aqui continuar errada lá. Voltar ao fluxo antigo é trocar a rota.
 */
export type PageMode = 'REGISTER' | 'IDENTIFY';

const STEPS_BY_MODE: Record<PageMode, readonly [string, string, string]> = {
    // "Dados do Recebimento" seria mentira no modo IDENTIFY: não se informa recebimento nenhum,
    // escolhe-se um que já existe
    REGISTER: ['Dados do Recebimento', 'Selecionar Contratos', 'Selecionar Faturas'],
    IDENTIFY: ['Selecionar Pagamento', 'Selecionar Contratos', 'Selecionar Faturas'],
};

const PAGE_TITLE: Record<PageMode, string> = {
    REGISTER: 'Registrar Recebimento',
    IDENTIFY: 'Identificação de Pagamento',
};

const PAGE_ROUTE: Record<PageMode, string> = {
    REGISTER: '/billing/register-payment',
    IDENTIFY: '/billing/payment-identification',
};

/**
 * O que fazer com as DEMAIS faturas abertas do título quando o pagamento for aprovado.
 *
 * Era um par de checkboxes — "Cancelar faturas abertas" e "Parar de cobrar faturas abertas" —, e
 * eles nunca foram independentes: cancelar já para de cobrar, então a tela precisava forçar o
 * segundo e travá-lo sempre que o primeiro era marcado. Três estados mutuamente exclusivos dizem a
 * mesma coisa sem regra escondida, e `''` dá à tela um estado "ainda não decidiu" que dois
 * booleanos não conseguiam expressar.
 */
export type OpenInvoicesChoice = '' | 'CANCEL' | 'UNCOLLECT' | 'KEEP';

/** Opções do select de destino das demais faturas abertas (o vazio vem do `emptyLabel`). */
const OPEN_INVOICES_ITEMS = [
    { value: 'CANCEL', label: 'Cancelar' },
    { value: 'UNCOLLECT', label: 'Parar de Cobrar' },
    { value: 'KEEP', label: 'Manter como estão' },
];

/**
 * A promessa que o rascunho por TÍTULO carrega. Sempre `_ALL`: aqui a linha é um título inteiro,
 * não uma fatura escolhida — o usuário decidiu o que fazer com a COBRANÇA daquele título, e as
 * irmãs de uma quebra em frações continuariam cobrando dívida já resolvida se o efeito fosse só
 * na fatura de origem.
 *
 * `''` não chega aqui: `canSubmit` bloqueia o envio enquanto alguma linha estiver sem escolha. Se
 * chegasse, NOTHING é a única saída honesta — não fazer nada é o que a tela não prometeu fazer.
 */
export const draftPromise = (choice: OpenInvoicesChoice): PaymentActionPromise => {
    if (choice === 'CANCEL') return 'CANCEL_ALL';
    if (choice === 'UNCOLLECT') return 'UNCOLLECT_ALL';
    return 'NOTHING';
};

/**
 * O caminho de volta do `draftPromise`: da promessa GRAVADA para a escolha da tela.
 *
 * Existe para reabrir uma marcação exatamente como ela foi salva. Sem isto, editar um item marcado
 * com "parar de cobrar" devolveria a linha sem escolha nenhuma, e o usuário reescolheria no escuro —
 * ou pior, escolheria outra coisa sem perceber que estava mudando.
 *
 * Aceita as formas sem `_ALL` porque a marcação pode ter vindo do modal da tela de Títulos, que usa
 * escopo de fatura: a intenção ("cancelar", "parar de cobrar") é a mesma, e é ela que a tela mostra.
 */
export const choiceFromPromise = (promise?: string | null): OpenInvoicesChoice => {
    if (promise === 'CANCEL' || promise === 'CANCEL_ALL') return 'CANCEL';
    if (promise === 'UNCOLLECT' || promise === 'UNCOLLECT_ALL') return 'UNCOLLECT';
    if (promise === 'NOTHING') return 'KEEP';
    return '';
};

/**
 * O rascunho e a marcação falam do MESMO título?
 *
 * ⚠️ Comparar `invoiceId` não serve, e foi o que quebrou o editar/remover em sandbox. A marcação
 * aponta para a fatura que ERA a original do título quando alguém marcou; o rascunho aponta para a
 * que é a original AGORA. Uma reemissão, um boleto trocado ou uma marcação feita pela tela de
 * Títulos (que escolhe uma fatura específica do título) fazem os dois ids divergirem — e a tela
 * dizia "não foi possível carregar este título para edição" sobre um título que estava bem ali.
 *
 * O par (operação, título) é o que o servidor usa para garantir "um título, uma marcação", então é
 * a mesma identidade dos dois lados.
 */
export const sameCharge = (a: any, b: any): boolean =>
    !!a?.creditOperationId && String(a.creditOperationId) === String(b?.creditOperationId)
    && String(a?.chargeId ?? '') === String(b?.chargeId ?? '');

/** Stepper das três etapas — vive no controlSlot do PageForm, grudado no cabeçalho. */
const Stepper: React.FC<{ step: number; steps: readonly string[] }> = ({ step, steps }) => (
    <div className="flex items-center">
        {steps.map((s, idx) => (
            <div key={s} className="flex items-center flex-1">
                <div className={`flex items-center gap-2 ${idx <= step ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${idx < step ? 'bg-green-500 text-white' : idx === step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {idx < step ? '✓' : idx + 1}
                    </span>
                    <span className="text-sm">{s}</span>
                </div>
                {idx < steps.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${idx < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </div>
        ))}
    </div>
);

const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// "2.500.000,00" | "2500000.00" → number
const parseBRL = (s: string) => {
    const cleaned = (s || '').trim();
    if (!cleaned) return 0;
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

// Valores iniciais do formulário — usados no useState E no reset pós-submit, para os
// dois nunca divergirem (era por isso que a tela voltava com dados da etapa 1).
/**
 * Chave de idempotência do pagamento (opcional no payload). `crypto.randomUUID` só
 * existe em contexto seguro, e o próprio global `crypto` pode não existir — sem esta
 * guarda o envio estourava "crypto is not defined" antes de chamar a API.
 */
const today = () => new Date().toISOString().slice(0, 10);
const INITIAL_TIME = '00:00';
/**
 * Vencimento, do MAIS RECENTE para o mais antigo.
 *
 * Era natureza + nº do título. O que o operador procura ao identificar um pagamento é a cobrança
 * que acabou de vencer, e ela ficava no fim de uma lista ordenada por outro critério.
 */
const INITIAL_INV_SORT = 'dueDate' as const;

/** Opções de ordenação da lista de títulos do passo 3. */
const INV_SORT_ITEMS = [
    { value: 'title', label: 'Natureza e nº do título' },
    { value: 'dueDate', label: 'Vencimento (mais recente)' },
    { value: 'amount', label: 'Valor' },
    { value: 'payer', label: 'Pagador' },
];

/**
 * Valor esperado para as dicas de desconto/acréscimo: o do RASCUNHO, que já é o saldo do título
 * (`amount - amount_paid`, resolvido no `goToInvoices`).
 *
 * ⚠️ Descontar `amountPaid` daqui subtraía o pago DUAS vezes — num título de 1000 com 300 pagos, o
 * campo carregava 700 e a dica cobrava 400, acusando acréscimo de 300 em pagamento correto.
 */
const invoiceExpected = (inv: Invoice) => round2(inv.amount);

/**
 * RASCUNHO de fatura — uma por TÍTULO (charge) com dívida viva, não uma por fatura existente.
 *
 * A troca de granularidade é o que faz "liquidar" e "parcial" voltarem a significar o que dizem:
 * o rascunho cobre o saldo INTEIRO do título, então não há fração a considerar nem faturas irmãs
 * a conciliar. `invoiceId` é a fatura ORIGINAL do charge — a de quem o rascunho deriva.
 */
interface Invoice {
    id: number;
    /** fatura ORIGINAL do charge: é dela que a fatura nova deriva */
    invoiceId: string;
    chargeId: string | null;
    creditOperationId: string;
    /** saldo do TÍTULO (amount - paid_amount): é o que o rascunho cobra */
    amount: number;
    /** valor NOMINAL do título, exibido abaixo do valor a cobrar */
    nominalAmount?: number | null;
    amountPaid: number | null;
    dueDate: string;
    status: string;
    statusFormatted?: string;
    documentTitle?: string | null;
    /** nº do título (fraciona a OPERAÇÃO: "Parcela 3", "Recebível 12") */
    installmentNumber?: number | null;
    contractLabel?: string | null;
    payer?: { name: string; documentNumber: string | null };
    /** cenário do charge — decide quais modos de liquidação a tela pode oferecer */
    chargeScenario?: string;
    allowsPartialPayment?: boolean;
    /** modo que a própria fatura declarava (tri-state; null = baldinho) */
    totalPayment?: boolean | null;
    /** o título já tem fatura com pagamento sinalizado (vw_charges_list.has_signalled_payment) */
    hasSignalledPayment?: boolean;
    _op?: string; // nº do contrato (para exibição)
    operationNumber?: string | null;   // a OPERAÇÃO — texto livre, ao lado do contrato
}

interface ItemEdit {
    value: string;          // BRL string editável
    /** TOTAL | PARCIAL | OPERATION_WATERFALL — convertido em total_payment tri-state no submit */
    mode: LiquidationMode;
    /** destino das DEMAIS faturas abertas do título; `''` = o usuário ainda não escolheu */
    promise: OpenInvoicesChoice;
    /**
     * Atributos da COBRANÇA (AF-3671 step 2) — só o modo IDENTIFY os coleta: eles viajam na
     * MARCAÇÃO, e o `process-payment` do ato único (deprecated) não tem onde levá-los.
     */
    payment_source: string;
    pending_guarantor_refund: boolean;
}

/**
 * Modos válidos para esta fatura. Sem `chargeScenario` (API antiga) cai em GUARANTEE, que só
 * aceita total — o mesmo que a tela fazia antes deste campo existir.
 */
const modesOf = (inv: Invoice): LiquidationMode[] =>
    PAYMENT_MODES_BY_SCENARIO[inv.chargeScenario || 'GUARANTEE'] || ['TOTAL'];

/** Valor pago da linha em número — a linha guarda BRL em texto. */
const editAmount = (e: ItemEdit): number => round2(parseBRL(e.value));

/** A linha ainda não disse o que fazer com as demais faturas abertas do título. */
const missingPromise = (e: ItemEdit): boolean => !e.promise;

/** A linha está sem valor: zero não aloca nada e o envio não tem o que levar. */
const missingAmount = (e: ItemEdit): boolean => editAmount(e) <= 0;

/** Falta ALGO nesta linha — é o que acende o destaque no card e o contador do acordeão. */
const rowIncomplete = (e: ItemEdit): boolean => missingPromise(e) || missingAmount(e);

interface Step3Pending {
    selectedCount: number;
    releasedCount: number;
    /** linhas sem destino escolhido */
    withoutPromise: number;
    /** linhas com valor zerado */
    withoutAmount: number;
    /** quanto a soma passa do valor recebido (0 quando não passa) */
    excess: number;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many.replace('{n}', String(n)));

/**
 * O que ainda falta para o passo 3 poder ser enviado, em linguagem de tela.
 *
 * Existe porque um botão desabilitado não explica nada: o motivo morava só num `title`, invisível
 * até alguém passar o mouse — e quem não passava ficava sem saber por que não dava para concluir.
 * A mesma contagem alimenta o destaque de cada campo, para a lista e os cards nunca discordarem.
 */
export const step3Pending = (p: Step3Pending): string[] => {
    const reasons: string[] = [];
    if (p.selectedCount === 0 && p.releasedCount === 0) {
        reasons.push('Selecione ao menos um título na área "Operações / Faturas", abaixo — ou remova uma marcação existente.');
    }
    if (p.withoutAmount > 0) {
        reasons.push(plural(p.withoutAmount,
            'Informe o "Valor pago" de 1 título selecionado — está zerado.',
            'Informe o "Valor pago" de {n} títulos selecionados — estão zerados.'));
    }
    if (p.withoutPromise > 0) {
        reasons.push(plural(p.withoutPromise,
            'Escolha em "Demais faturas abertas" o destino da cobrança de 1 título selecionado.',
            'Escolha em "Demais faturas abertas" o destino da cobrança de {n} títulos selecionados.'));
    }
    if (p.excess > 0) {
        reasons.push(`A soma alocada passa do valor recebido em R$ ${fmt(p.excess)} — reduza os valores ou tire um título da seleção.`);
    }
    return reasons;
};

interface Props {
    /** default REGISTER para as rotas e testes existentes seguirem valendo sem mudança */
    mode?: PageMode;
}

const RegisterPaymentPage: React.FC<Props> = ({ mode = 'REGISTER' }) => {
    const identifying = mode === 'IDENTIFY';
    const STEPS = STEPS_BY_MODE[mode];
    const [step, setStep] = useState(0);
    /** modo IDENTIFY: o pagamento já registrado que este trabalho vai identificar */
    const [selectedPayment, setSelectedPayment] = useState<PaymentRow | null>(null);
    const [infoPayment, setInfoPayment] = useState<PaymentRow | null>(null);
    /**
     * Chave do pagamento, estável durante o preenchimento. Gerada aqui e NÃO no submit: dois
     * cliques em "Concluir" (ou um retry depois de timeout) mandavam ids diferentes e nasciam dois
     * pagamentos, porque a idempotência do consumidor é por chave. O reset da tela sorteia a
     * próxima.
     */
    const [paymentId, setPaymentId] = useState(() => newPaymentId());

    // ── Step 1 ──
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(today);
    const [time, setTime] = useState(INITIAL_TIME);
    const [showTime, setShowTime] = useState(false);
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(emptyPaymentTypeValue());
    const [notes, setNotes] = useState('');
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [receiptName, setReceiptName] = useState('');
    const [payer, setPayer] = useState<PayerValue>(() => emptyPayer());
    // "Responsável" não é mais campo de tela: é quem está logado realizando a ação
    const auth = useContext(AuthContext);
    const userResponsible = auth?.user?.username || auth?.user?.email || '';

    // ── Step 2 ──
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<SettlementOperation[]>([]);
    const [opFilters, setOpFilters] = useState<AppliedFilter[]>([]);
    const [opPage, setOpPage] = useState(0);
    const [opTotal, setOpTotal] = useState(0);
    const [opSearched, setOpSearched] = useState(false);
    const [selectedOps, setSelectedOps] = useState<SettlementOperation[]>([]);

    // ── Step 3 ──
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [edits, setEdits] = useState<Record<string, ItemEdit>>({}); // key = invoiceId (selecionados)
    const [invSearch, setInvSearch] = useState('');
    const [invSort, setInvSort] = useState<'title' | 'dueDate' | 'amount' | 'payer'>(INITIAL_INV_SORT);
    const [submitting, setSubmitting] = useState(false);
    // resumo da operação (contrato/aditamento/valor/pago) p/ cabeçalho dos grupos — via settlement /operations
    const [openSel, setOpenSel] = useState(true);   // accordion: faturas selecionadas
    const [openList, setOpenList] = useState(true);  // accordion: listagem/seleção
    /**
     * O que este pagamento JÁ tem: marcações vivas e faturas efetivadas.
     *
     * Vem de `listInvoicesByPayment`, que devolve as duas coisas — `paymentSignalled` separa uma da
     * outra. É o que permite voltar a um pagamento parcialmente identificado e continuar de onde
     * parou, em vez de recomeçar sem saber o que já foi feito.
     */
    const [allocations, setAllocations] = useState<any[]>([]);
    /**
     * Marcações que o usuário tirou (ou mandou editar) NESTA sessão.
     *
     * ⚠️ Só vira escrita no ENVIO. Sair da tela sem enviar devolve tudo como estava — é a mesma
     * promessa do resto do passo 3, onde nada é gravado até o "Concluir". Guardar id e não o objeto
     * porque é isso que viaja no `release` do payload.
     */
    const [released, setReleased] = useState<string[]>([]);
    // nasce COLAPSADO: é contexto, não a tarefa. Quem chega ao passo 3 vem marcar faturas, e
    // uma lista aberta empurra a área de trabalho para baixo da dobra
    const [openMarked, setOpenMarked] = useState(false);  // accordion: já marcado
    const [bulkOpen, setBulkOpen] = useState(false); // modal "Ações em Lote"

    /**
     * Catálogo de Fontes do Pagamento (`business_properties.PAYMENT_SOURCES`).
     *
     * Só no modo IDENTIFY: fonte e aval a devolver viajam na MARCAÇÃO, e o payload do ato único
     * (deprecated) não tem onde levá-los. A fatura que ele cria nasce sem fonte e o `settle_invoice`
     * aplica o default, como sempre fez.
     */
    const [sources, setSources] = useState<PaymentSourceOption[]>([]);

    useEffect(() => {
        if (!identifying) return;
        let alive = true;
        listPaymentSources()
            .then(list => { if (alive) setSources(list); })
            .catch(() => { /* sem catálogo o select fica vazio e o usuário escolhe nada */ });
        return () => { alive = false; };
    }, [identifying]);

    /**
     * Preenche a fonte das linhas que nasceram antes do catálogo chegar.
     *
     * ⚠️ Sem isto o select mostraria a PRIMEIRA opção com o estado em `''` — desde que a opção
     * vazia saiu, não há mais o que renderizar para o vazio, e a tela afirmaria uma fonte que o
     * payload não leva. Na prática o catálogo chega durante as etapas 1 e 2 e este efeito não
     * encontra nada para corrigir; ele existe para o caso em que a rota demora.
     */
    useEffect(() => {
        if (!sources.length) return;
        setEdits(prev => {
            const pending = Object.keys(prev).filter(key => !prev[key].payment_source);
            if (!pending.length) return prev;
            const next = { ...prev };
            for (const key of pending) {
                const inv = invoices.find(i => i.invoiceId === key);
                next[key] = {
                    ...next[key],
                    payment_source: defaultPaymentSource(sources, inv?.chargeScenario),
                };
            }
            return next;
        });
    }, [sources, invoices]);

    /**
     * O valor que precisa ser todo alocado.
     *
     * No modo IDENTIFY ele vem do PAGAMENTO escolhido, não de um campo: o dinheiro já entrou e o
     * valor dele é fato gravado. Deixar o usuário digitar aqui permitiria fechar a conta contra um
     * número que não é o que o banco recebeu.
     */
    const paymentAmount = identifying
        ? round2(Number(selectedPayment?.amount || 0))
        : round2(parseBRL(amount));

    // IDENTIFY: basta ter escolhido um pagamento — os dados dele já foram validados no passo 1.
    // REGISTER: conta de recebimento só é exigida em pagamento financeiro.
    /**
     * O que este pagamento já tem, buscado ao ESCOLHÊ-LO.
     *
     * Aqui e não no passo 3 porque o passo 2 já precisa saber: é daqui que sai a lista de operações
     * a carregar junto. Trocar de pagamento zera o que foi removido nesta sessão — `released` é
     * sobre AQUELE pagamento, e carregá-lo para outro removeria marcação alheia.
     */
    useEffect(() => {
        if (!identifying || !selectedPayment) {
            setAllocations([]);
            setReleased([]);
            return;
        }
        let alive = true;
        /**
         * ⚠️ Trocar de pagamento zera os passos 2 e 3 INTEIROS.
         *
         * O estado sobreviver ao vai-e-vem entre 2 e 3 é desejável — quem volta para mexer nas
         * operações não quer perder o que já montou. Mas voltar ao passo 1 e escolher OUTRO
         * pagamento é começar outro trabalho: as faturas selecionadas, os valores e as operações
         * eram daquele dinheiro. Mantidas, o usuário chegava ao passo 3 com o lote do pagamento
         * anterior já montado — e marcava para o pagamento errado sem perceber.
         */
        setReleased([]);
        setEdits({});
        setInvoices([]);
        setSelectedOps([]);
        setResults([]);
        setOpFilters([]);
        setOpPage(0);
        setOpTotal(0);
        setOpSearched(false);
        setInvSearch('');
        setOpenMarked(false);
        listInvoicesByPayment(selectedPayment.paymentId)
            .then(list => { if (alive) setAllocations(list || []); })
            // sem isto a tela continua marcando normalmente: a seção "Já Marcado" fica vazia, o que
            // é pior do que travar o passo 3 inteiro por causa de uma lista de contexto
            .catch(() => { if (alive) setAllocations([]); });
        return () => { alive = false; };
    }, [identifying, selectedPayment]);

    /**
     * O que ainda falta identificar do pagamento escolhido.
     *
     * ⚠️ Os passos 1 e 2 anunciavam o valor CHEIO como "a conta a fechar", e num pagamento já
     * parcialmente identificado isso é uma conta que não existe mais — o usuário chegava ao passo 3
     * procurando títulos para um valor que já tinha dono. As duas telas passam a mostrar os dois
     * números, e o que manda é o aberto.
     */
    const paymentPending = identifying && selectedPayment
        ? round2(selectedPayment.pendingAmount
            ?? (Number(selectedPayment.amount || 0) - Number(selectedPayment.markedAmount || 0)))
        : paymentAmount;
    const partiallyIdentified = identifying && !!selectedPayment
        && round2(paymentPending) !== round2(paymentAmount);

    const step1Valid = identifying
        ? !!selectedPayment && paymentAmount > 0
        : paymentAmount > 0 && !!date
        && (typeFields.payment_type !== PaymentType.FINANCIAL || !!typeFields.receiving_bank_account)
        && onlyDigits(payer.document).length >= 11;

    // ─── Step 2: busca de operações (settlement) ─────────────────────────────
    // GET /operations (settlement_v2.vw_operations_list). Vem daqui e não do contracts_management
    // porque nesta tela a operação interessa pelo que ela DEVE — `amount`, a soma dos títulos — e o
    // contracts_management só conhece o `nominal_value` do credit, que não responde por isso.
    //
    // `status = 'Finalizado com sucesso'` e `billed IS TRUE` já são chumbados no servidor (sem
    // faturamento não existe fatura para receber), então não vão mais como filtro fixo.
    const OP_PAGE_SIZE = 20;
    const fetchOperations = useCallback(async (filters: AppliedFilter[], page: number) => {
        setSearching(true);
        try {
            const params = serializeFilters(filters);
            // o settlement pagina em 1-based (`page`/`size`); o FilterBuilder entrega 0-based
            params.set('page', String(page + 1));
            params.set('size', String(OP_PAGE_SIZE));
            params.set('sort', '-sequencial_number');
            const data = await listOperations(Object.fromEntries(params));
            setResults(data.list);
            setOpTotal(data.totalElements);
            setOpSearched(true);
        } catch (e: any) {
            toast.error(`Erro ao buscar operações: ${e.message}`);
        } finally {
            setSearching(false);
        }
    }, []);

    const runSearch = useCallback((filters: AppliedFilter[]) => {
        setOpFilters(filters);
        setOpPage(0);
        fetchOperations(filters, 0);
    }, [fetchOperations]);

    const changeOpPage = (page: number) => {
        setOpPage(page);
        fetchOperations(opFilters, page);
    };

    // O participante preferencial (ASSIGNOR/BORROWER) a view já resolve — issuer_name/document.
    // O RÓTULO não usa o contract_label pronto de propósito: ele é sempre "n-a", e esta tela
    // omite o aditamento zero. Trocar mudaria o que o operador lê na busca, sem ninguém pedir.
    const opLabel = (op: SettlementOperation) => {
        const contract = op.sequencial_number ?? '—';
        return op.amendment_number && op.amendment_number > 0 ? `${contract}-${op.amendment_number}` : `${contract}`;
    };

    const toggleOp = (op: SettlementOperation) => {
        setSelectedOps(prev => prev.find(o => o.credit_operation_id === op.credit_operation_id)
            ? prev.filter(o => o.credit_operation_id !== op.credit_operation_id)
            : [...prev, op]);
    };
    const isOpSelected = (op: SettlementOperation) => !!selectedOps.find(o => o.credit_operation_id === op.credit_operation_id);

    /**
     * "Selecionar Tudo" vale pela PÁGINA que está na tela, não pelo total encontrado.
     *
     * A busca pagina de 20 em 20 e o cliente só tem a página corrente — prometer "tudo" incluiria
     * operações que ele não carregou. O contador ao lado mostra quantas a seleção tem de verdade.
     *
     * ⚠️ Desmarcar tira só as DESTA página. A seleção atravessa a paginação de propósito (o
     * `changeOpPage` não a limpa), então um `setSelectedOps([])` apagaria em silêncio o que o
     * usuário escolheu em outra página.
     */
    const allOnPageSelected = results.length > 0 && results.every(isOpSelected);
    const someOnPageSelected = results.some(isOpSelected);

    const toggleAllOnPage = (select: boolean) => {
        setSelectedOps(prev => {
            const onPage = new Set(results.map(o => o.credit_operation_id));
            const kept = prev.filter(o => !onPage.has(o.credit_operation_id));
            return select ? [...kept, ...results] : kept;
        });
    };

    // ─── Step 3: um RASCUNHO por título com dívida viva ──────────────────────
    // Antes listava as faturas OPEN de cada operação (1 linha = 1 fatura) e filtrava no cliente.
    // Agora parte dos CHARGES com título OPEN/PARTIALLY_PAID: 1 linha = 1 título, e cada linha é o
    // rascunho da fatura que vai ser criada, pelo saldo inteiro daquele título. O filtro de status
    // é do TÍTULO e vai para o servidor — o `size` deixou de cortar antes de filtrar.
    const goToInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const all: Invoice[] = [];
            /**
             * As operações escolhidas no passo 2, MAIS as que já têm marcação neste pagamento.
             *
             * Sem as segundas, remover um item de "Já Marcado" não teria para onde devolvê-lo: a
             * lista de seleção não conteria aquele título, e editar não acharia o rascunho. O
             * usuário veria o item sumir sem explicação.
             */
            const fromAllocations = allocations
                .filter(a => a.paymentSignalled && a.creditOperationId)
                .map(a => ({ credit_operation_id: a.creditOperationId } as SettlementOperation));
            const ops = [...selectedOps];
            for (const extra of fromAllocations) {
                if (!ops.some(o => o.credit_operation_id === extra.credit_operation_id)) {
                    ops.push(extra);
                }
            }
            for (const op of ops) {
                const data = await listOpenChargesByOperation(op.credit_operation_id);
                (data.items || data.list || [])
                    // sem fatura original não há de quem derivar (charge que nunca foi faturado)
                    .filter((c: any) => c.originalInvoiceId)
                    .forEach((c: any) => all.push({
                        id: 0,
                        invoiceId: c.originalInvoiceId,
                        chargeId: c.chargeId,
                        creditOperationId: c.creditOperationId,
                        amount: Number(c.remainingAmount ?? c.amount ?? 0),
                        nominalAmount: c.amount,
                        amountPaid: c.paidAmount,
                        dueDate: c.dueDate,
                        status: c.titleDebtStatus || 'OPEN',
                        documentTitle: c.documentTitle,
                        installmentNumber: c.installmentNumber,
                        contractLabel: c.contractLabel,
                        payer: c.payer,
                        chargeScenario: c.chargeScenario,
                        allowsPartialPayment: true,
                        totalPayment: undefined,
                        // o título já tem marcação pendente de outro pagamento (changelog_024).
                        // Sem carregar aqui, a linha entraria selecionável e o usuário montaria o
                        // lote todo para receber a recusa do servidor no fim.
                        hasSignalledPayment: !!c.hasSignalledPayment,
                        _op: op.operation_number ?? undefined,   // ausente nas operações vindas das marcações
                    }));
            }
            setInvoices(all);
            setStep(2);
        } catch (e: any) {
            toast.error(`Erro ao carregar faturas: ${e.message}`);
        } finally {
            setLoadingInvoices(false);
        }
    };

    // o default é o modo que a PRÓPRIA fatura declarava, limitado ao que o cenário aceita:
    // uma parcela paga por garantia nasce baldinho, e forçar TOTAL aqui liquidaria o título
    const defaultEdit = (inv: Invoice): ItemEdit => ({
        value: fmt(inv.amount),
        mode: clampMode(modeOf(inv.totalPayment), modesOf(inv)),
        // Nasce SEM escolha, de propósito. O par de checkboxes vinha MARCADO, e cancelar as
        // faturas abertas do título é a mais destrutiva das três opções: um default que ninguém
        // leu vira decisão que ninguém tomou. `canSubmit` cobra a escolha de cada linha.
        promise: '',
        // pré-selecionada pela NATUREZA do título — a mesma regra do `default_if` do seed
        payment_source: defaultPaymentSource(sources, inv.chargeScenario),
        pending_guarantor_refund: false,
    });

    /**
     * Reabre uma marcação existente para edição.
     *
     * O item SAI de "Já Marcado" e entra em "Faturas selecionadas" com tudo o que estava gravado —
     * valor, modo, promessa, fonte, aval. Ele não volta para "Já Marcado": a partir daqui é um item
     * como os outros, e sair sem enviar o traz de volta inteiro.
     *
     * Precisa do RASCUNHO correspondente (`invoices`): é ele que a seção de selecionados renderiza.
     * Por isso o `goToInvoices` carrega também os títulos das operações que já têm marcação neste
     * pagamento, mesmo que o usuário não as tenha escolhido no passo 2.
     */
    const editMarking = (item: any) => {
        const draft = invoices.find(inv => sameCharge(inv, item));
        if (!draft) {
            toast.warn('Não foi possível carregar este título para edição. Remova e marque de novo.');
            return;
        }
        const marking = item.marking || {};
        setReleased(prev => (prev.includes(item.invoiceId) ? prev : [...prev, item.invoiceId]));
        setEdits(prev => ({
            ...prev,
            // a chave é a do RASCUNHO: é ele que a seção de selecionadas renderiza e é o
            // `invoice_id` dele que vai no payload. O id liberado é o da marcação ANTIGA
            [draft.invoiceId]: {
                value: fmt(Number(marking.value ?? item.amountPaid ?? 0)),
                mode: clampMode(modeOf(marking.totalPayment), modesOf(draft)),
                promise: choiceFromPromise(marking.paymentActionPromise),
                payment_source: marking.paymentSource
                    || defaultPaymentSource(sources, draft.chargeScenario),
                pending_guarantor_refund: !!marking.pendingGuarantorRefund,
            },
        }));
        setOpenSel(true);
    };

    /**
     * Tira uma marcação existente do pagamento.
     *
     * O título volta a ficar SELECIONÁVEL na lista de baixo — não volta para "Já Marcado". E nada
     * disso é gravado até o envio: saindo da tela sem enviar, a marcação continua lá.
     */
    const removeMarking = (item: any) => {
        setReleased(prev => (prev.includes(item.invoiceId) ? prev : [...prev, item.invoiceId]));
        // se estava aberto para edição, o rascunho sai junto — remover vence editar. A chave do
        // `edits` é o id do RASCUNHO, que pode não ser o da marcação (ver `sameCharge`)
        const draft = invoices.find(inv => sameCharge(inv, item));
        setEdits(prev => {
            const next = { ...prev };
            delete next[item.invoiceId];
            if (draft) delete next[draft.invoiceId];
            return next;
        });
    };

    const toggleInvoice = (inv: Invoice) => {
        setEdits(prev => {
            const next = { ...prev };
            if (next[inv.invoiceId]) delete next[inv.invoiceId];
            else next[inv.invoiceId] = defaultEdit(inv);
            return next;
        });
    };

    const patchEdit = (inv: Invoice, patch: Partial<ItemEdit>) => {
        setEdits(prev => {
            const cur = prev[inv.invoiceId];
            if (!cur) return prev;
            let next = { ...cur, ...patch };
            // value alterado → ajusta o modo automaticamente (usuário ainda pode remarcar).
            // Duas exceções: OPERATION_WATERFALL é uma DECLARAÇÃO da fatura (o dinheiro entra na operação,
            // não no título) e não se troca por comparação de valor; e cenário que não aceita
            // PARCIAL (garantia) não pode ser rebaixado.
            const allowedModes = modesOf(inv);
            if (patch.value !== undefined && cur.mode !== 'OPERATION_WATERFALL') {
                const v = round2(parseBRL(patch.value));
                const target: LiquidationMode = v < inv.amount ? 'PARTIAL' : 'TOTAL';
                if (allowedModes.includes(target)) next.mode = target;
            }
            // trocar para uma fonte que não admite devolução desmarca o aval: deixar marcado
            // mandaria ao backend um `true` que aquela fonte não comporta
            if (patch.payment_source !== undefined
                && !allowsGuarantorRefund(sources, patch.payment_source)) {
                next.pending_guarantor_refund = false;
            }
            return { ...prev, [inv.invoiceId]: next };
        });
    };

    const isSelected = (inv: Invoice) => !!edits[inv.invoiceId];

    /**
     * Aplica UMA escolha a todas as faturas selecionadas.
     *
     * ⚠️ "Marcar Aval a Devolver" pula as linhas cuja fonte não admite devolução — e o resultado
     * DIZ quantas pulou. Sem isso o usuário lê "N atualizadas" como "todas", e só descobriria a
     * diferença conferindo card a card.
     */
    const applyBulk = (action: BulkAction) => {
        const entries = Object.entries(edits);
        // contado FORA do updater: o React pode reexecutá-lo, e o total dobraria
        const skipped = action.kind === 'REFUND' && action.on
            ? entries.filter(([, e]) => !allowsGuarantorRefund(sources, e.payment_source)).length
            : 0;
        setEdits(prev => {
            const next = { ...prev };
            for (const [key, cur] of Object.entries(prev)) {
                if (action.kind === 'PROMISE') {
                    next[key] = { ...cur, promise: action.promise };
                } else if (action.kind === 'SOURCE') {
                    next[key] = {
                        ...cur,
                        payment_source: action.alias,
                        // a fonte nova pode não admitir aval — mesma regra do `patchEdit`
                        pending_guarantor_refund: allowsGuarantorRefund(sources, action.alias)
                            && cur.pending_guarantor_refund,
                    };
                } else if (!action.on || allowsGuarantorRefund(sources, cur.payment_source)) {
                    next[key] = { ...cur, pending_guarantor_refund: action.on };
                }
            }
            return next;
        });
        setBulkOpen(false);
        toast.success(`${entries.length - skipped} fatura(s) atualizada(s)`
            + (skipped ? ` · ${skipped} sem fonte que admite aval, mantida(s) como estava(m)` : ''));
    };

    const filteredInvoices = useMemo(() => {
        const q = invSearch.trim().toLowerCase();
        let list = invoices;
        if (q) {
            list = list.filter(inv =>
                (inv.payer?.name || '').toLowerCase().includes(q) ||
                (inv.chargeId || '').toLowerCase().includes(q) ||
                (inv.documentTitle || '').toLowerCase().includes(q) ||
                (inv._op || '').toLowerCase().includes(q));
        }
        const sorted = [...list].sort((a, b) => {
            if (invSort === 'amount') return b.amount - a.amount;
            if (invSort === 'payer') return (a.payer?.name || '').localeCompare(b.payer?.name || '');
            if (invSort === 'title') {
                // agrupa por natureza e ordena pelo nº do título: as 16 garantias saem em sequência,
                // as parcelas em outra — ler "Recebível 1..16" na ordem é o que o usuário confere
                const byScenario = (a.chargeScenario || '').localeCompare(b.chargeScenario || '');
                if (byScenario !== 0) return byScenario;
                return (a.installmentNumber ?? 0) - (b.installmentNumber ?? 0);
            }
            // DESCENDENTE: o vencimento mais recente primeiro — ver INITIAL_INV_SORT
            return (b.dueDate || '').localeCompare(a.dueDate || '');
        });
        return sorted;
    }, [invoices, invSearch, invSort]);

    // agrupa os invoices filtrados por operação, preservando a ordem das operações selecionadas
    const groups = useMemo(() => {
        const byOp: Record<string, Invoice[]> = {};
        for (const inv of filteredInvoices) (byOp[inv.creditOperationId] ||= []).push(inv);
        return selectedOps
            .map(op => ({ op, list: byOp[op.credit_operation_id] || [] }))
            .filter(g => g.list.length > 0);
    }, [filteredInvoices, selectedOps]);

    // invoices selecionados (na ordem em que foram carregados)
    const selectedInvoices = useMemo(() => invoices.filter(inv => edits[inv.invoiceId]), [invoices, edits]);

    const allocated = useMemo(() =>
        round2(Object.values(edits).reduce((s, e) => s + parseBRL(e.value), 0)), [edits]);

    // ─── o que este pagamento já carrega (AF-3671 step 4) ────────────────────
    /** Efetivadas: carimbadas na fatura, fora de alcance — nem editar, nem remover. */
    const settledItems = useMemo(
        () => allocations.filter(a => !a.paymentSignalled && a.status === 'PAID'), [allocations]);
    /** Marcadas e ainda não tocadas nesta sessão. Quem foi editado ou removido sai daqui. */
    const markedItems = useMemo(
        () => allocations.filter(a => a.paymentSignalled && !released.includes(a.invoiceId)),
        [allocations, released]);

    const sumOf = (list: any[]) => round2(list.reduce((s, a) => s + Number(a.amountPaid || 0), 0));
    const settledAmount = useMemo(() => sumOf(settledItems), [settledItems]);
    const markedAmount = useMemo(() => sumOf(markedItems), [markedItems]);

    /** Tudo o que tem destino depois deste envio: o que já estava + o que está sendo alocado. */
    const committed = round2(settledAmount + markedAmount + allocated);
    const over = committed > paymentAmount;
    /** As quatro fatias da barra, em % do valor TOTAL do pagamento. */
    const slices = useMemo(() => {
        if (paymentAmount <= 0) return { settled: 0, marked: 0, now: 0 };
        const p = (v: number) => (v / paymentAmount) * 100;
        return { settled: p(settledAmount), marked: p(markedAmount), now: p(allocated) };
    }, [paymentAmount, settledAmount, markedAmount, allocated]);
    const complete = round2(committed) === paymentAmount;

    // toda linha precisa dizer o que fazer com as demais faturas abertas do título: o select
    // nasce vazio, e um envio com `''` viraria NOTHING sem ninguém ter escolhido — ver `defaultEdit`
    const allPromised = Object.values(edits).every(e => !missingPromise(e));
    /**
     * ⚠️ A condição era `allocated === paymentAmount`: a tela SÓ deixava enviar com 100% alocado.
     * É o que a marcação parcial derruba — marcar o que se sabe, salvar, e voltar depois. Enviar
     * ACIMA do valor continua proibido: o servidor recusa, e a conta não existe.
     *
     * Há o que enviar quando algum item foi alocado OU alguma marcação foi removida — remover sem
     * acrescentar nada é um pedido legítimo.
     */
    const hasSomethingToSend = Object.keys(edits).length > 0 || released.length > 0;
    const canSubmit = paymentAmount > 0 && !over && hasSomethingToSend && allPromised
        && (allocated > 0 || released.length > 0);

    /**
     * O que falta para concluir — a MESMA verdade do `canSubmit`, dita em voz alta.
     *
     * Quantas linhas estão incompletas também vira contador no acordeão: com ele fechado, o usuário
     * não tinha como saber que a pendência estava lá dentro.
     */
    const editList = Object.values(edits);
    const rowsIncomplete = editList.filter(rowIncomplete).length;
    const pendingReasons = step3Pending({
        selectedCount: editList.length,
        releasedCount: released.length,
        withoutPromise: editList.filter(missingPromise).length,
        withoutAmount: editList.filter(missingAmount).length,
        excess: over ? round2(committed - paymentAmount) : 0,
    });

    // ─── Submit ──────────────────────────────────────────────────────────────
    const buildPayload = () => {
        const payment_date = toIsoInstant(date, time, showTime)!; // wall-clock local → UTC (sem D±1)
        const items: PaymentInvoiceItem[] = invoices
            .filter(inv => edits[inv.invoiceId])
            .map(inv => {
                const e = edits[inv.invoiceId];
                return {
                    credit_operation_id: inv.creditOperationId,
                    charge_id: inv.chargeId,
                    invoice_id: inv.invoiceId,
                    value: round2(parseBRL(e.value)),
                    total_payment: MODE_TO_TOTAL_PAYMENT[e.mode],
                    // Pagamento parcial: a fatura nova cobre uma PARTE do título, não ele inteiro.
                    // Herdar o 1/1 da original faria a cobrança parecer completa, e o charge não
                    // apareceria como "Falta faturar" apesar do saldo devedor. 1/2 é a afirmação
                    // mínima de "isto é um pedaço" — quantas faturas virão ainda não se sabe, e o
                    // que a tela de Títulos precisa saber é que falta alguma.
                    ...(e.mode === 'PARTIAL'
                        ? { installment_number: 1, total_installments: 2 } : {}),
                    // o contrato do ato único ainda fala em dois booleanos; a TELA já não,
                    // e cancelar implica descobrar (era o acoplamento dos checkboxes antigos)
                    cancel_invoice: e.promise === 'CANCEL',
                    uncollect: e.promise === 'CANCEL' || e.promise === 'UNCOLLECT',
                    // Aqui a linha é um RASCUNHO por TÍTULO, não uma fatura: o usuário não
                    // escolheu qual cancelar, escolheu o que fazer com a cobrança daquele
                    // título. O default do backend é a fatura sozinha — quem age sobre uma
                    // fatura específica (modal de Liquidar/Baixa Parcial) não manda nada.
                    cancel_scope: 'CHARGE' as const,
                };
            });
        // o bloco do DINHEIRO é compartilhado com o modal Registrar Pagamento (passo 1):
        // duplicá-lo faria uma regra corrigida aqui continuar errada lá
        return {
            ...buildPaymentBlock({
                amount: paymentAmount,
                paymentDate: payment_date,
                typeFields,
                payer,
                paymentId,
                notes,
                receiptName,
                userResponsible,
            }),
            invoices: items,
            // o builder devolve o bloco solto (Record) para servir aos dois chamadores; a forma
            // completa só existe quando as faturas entram, e é aqui que ela se fecha
        } as ProcessPaymentPayload;
    };

    /** Volta a tela inteira (as 3 etapas) ao estado inicial. */
    const resetForm = () => {
        setStep(0);
        setPaymentId(newPaymentId());   // concluído: o próximo registro é outro comando
        // o pagamento identificado sai da fila de pendentes; manter a escolha faria a tela
        // oferecer um pagamento que o servidor já vai recusar
        setSelectedPayment(null);
        // etapa 1
        setAmount(''); setDate(today()); setTime(INITIAL_TIME); setShowTime(false);
        setTypeFields(emptyPaymentTypeValue()); setNotes(''); setReceiptName(''); setReceiptFile(null);
        setPayer(emptyPayer());
        // etapa 2
        setResults([]); setOpFilters([]); setOpPage(0); setOpTotal(0);
        setOpSearched(false); setSelectedOps([]);
        // etapa 3
        setInvoices([]); setEdits({}); setInvSearch(''); setInvSort(INITIAL_INV_SORT);
        setOpenSel(true); setOpenList(true); setOpenMarked(true);
        setAllocations([]); setReleased([]);
        // PayerFields, FilterBuilder e o select de conta têm estado interno próprio, mas
        // desmontam ao trocar de etapa — voltar para a etapa 1 já os remonta limpos.
    };

    /** Itens da MARCAÇÃO (modo IDENTIFY) — o mesmo recorte por título do payload antigo. */
    const buildMarkItems = (): MarkInvoiceItem[] => invoices
        .filter(inv => edits[inv.invoiceId])
        .map(inv => {
            const e = edits[inv.invoiceId];
            return {
                invoice_id: inv.invoiceId,
                value: round2(parseBRL(e.value)),
                payment_action_promise: draftPromise(e.promise),
                total_payment: MODE_TO_TOTAL_PAYMENT[e.mode],
                payment_source: e.payment_source || undefined,
                // só viaja quando a fonte admite: marcar aval num pagamento do sacado não
                // significa nada, e o backend guardaria um `true` que ninguém sabe interpretar
                pending_guarantor_refund:
                    allowsGuarantorRefund(sources, e.payment_source)
                    && e.pending_guarantor_refund,
                // mesma razão do fluxo antigo: a fatura nova cobre uma PARTE do título, e herdar
                // o 1/1 faria a cobrança parecer completa apesar do saldo devedor
                ...(e.mode === 'PARTIAL'
                    ? { installment_number: 1, total_installments: 2 } : {}),
                notes: notes || undefined,
            };
        });

    const submit = async () => {
        setSubmitting(true);
        try {
            if (identifying) {
                /**
                 * Uma chamada só, com as liberações JUNTO das marcações.
                 *
                 * O servidor libera antes de validar, no mesmo ato: é o que faz a substituição
                 * funcionar (sem isso o guard "um título, uma marcação" recusaria o item que a
                 * própria tela está devolvendo). E em duas chamadas, uma falha na segunda deixaria
                 * a marcação antiga apagada e a nova nunca gravada — perda silenciosa de trabalho,
                 * no meio de uma tela que promete não gravar nada até aqui.
                 */
                const res = await markInvoices(
                    selectedPayment!.paymentId, buildMarkItems(), { release: released });
                const n = res?.data?.marked?.length ?? res?.marked?.length ?? 0;
                const removidas = res?.data?.released?.length ?? res?.released?.length ?? 0;
                toast.success([
                    n > 0 ? `${n} fatura(s) sinalizada(s).` : null,
                    removidas > 0 ? `${removidas} sinalização(ões) removida(s).` : null,
                    n > 0 ? 'Aguardando aprovação do financeiro.' : null,
                ].filter(Boolean).join(' '));
                resetForm();
                return;
            }
            // comprovante primeiro: o nome canônico vem do backend e viaja no payload
            if (receiptFile) {
                const uploaded = await uploadPaymentReceipt(
                    paymentId, receiptFile, toIsoInstant(date, time, showTime)!, payer.name,
                );
                setReceiptName(uploaded.fileName);
            }
            const res = await processPayment(buildPayload());
            const n = res?.data?.settled?.length ?? 0;
            toast.success(`Pagamento registrado — ${n} fatura(s) processada(s)`);
            resetForm();
        } catch (e: any) {
            toast.error(`Erro ao registrar pagamento: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const input = 'w-full border rounded px-3 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-slate-800 disabled:cursor-not-allowed';
    const label = 'text-xs text-gray-500 dark:text-gray-400 block mb-1';

    const fmtDate = (d?: string) => (d ? d.split('-').reverse().join('/') : '—');

    // cabeçalho enriquecido do invoice (documento · pagador … valor / Fatura · op · Recebível · vence)
    /**
     * Cabeçalho de um item JÁ marcado ou efetivado, no mesmo molde do `invHeader`.
     *
     * Mesma informação, fonte diferente: aqui a linha vem do payload de FATURA (o que a
     * `listInvoicesByPayment` devolve), e não de um rascunho de título. Antes mostrava só contrato e
     * vencimento, e quem voltava a um pagamento não reconhecia o que tinha marcado — faltava
     * pagador, Título/NF e natureza, que é justamente por onde se procura uma cobrança.
     */
    const allocationHeader = (item: any) => (
        <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-2">
                <span className="text-sm font-medium truncate">
                    {item.payer?.documentNumber ? `${formatTaxId(item.payer.documentNumber)} · ` : ''}
                    {item.payer?.name || '—'}
                </span>
                <span className="text-sm whitespace-nowrap">
                    <span className="text-xs text-gray-500">
                        Título/NF {item.details?.documentTitle || '-'} · vence {fmtDate(item.dueDate)} ·{' '}
                    </span>
                    <span className="font-medium">R$ {fmt(Number(item.amountPaid || 0))}</span>
                </span>
            </div>
            <div className="flex justify-between gap-2 text-xs text-gray-500">
                <span className="truncate">
                    {/* CONTRATO e OPERAÇÃO são identificadores DIFERENTES e agora aparecem
                        separados: este bloco dizia "Operação 330-0" mostrando o CONTRATO, que é o
                        mesmo engano que a coluna "Contrato" da tela de Faturas tinha. */}
                    Contrato {item.contractLabel || (item.creditOperationId || '').slice(0, 8)}
                    {item.operationNumber && <> · Operação {item.operationNumber}</>}
                    {item.chargeId ? ` · #${String(item.chargeId).slice(0, 8)}` : ''}
                    {item.businessReference && (
                        <> · {CHARGE_SCENARIO_LABELS[item.businessReference] || item.businessReference}</>
                    )}
                    {item.titleInstallmentNumber != null && <> · Nº {item.titleInstallmentNumber}</>}
                </span>
                {/* o NOMINAL da fatura ao lado do que foi marcado: sem ele não se vê que a
                    marcação cobre só uma parte */}
                <span className="whitespace-nowrap">
                    {item.amount != null && Number(item.amount) !== Number(item.amountPaid || 0) && (
                        <>Valor Nominal R$ {fmt(Number(item.amount))}</>
                    )}
                </span>
            </div>
        </div>
    );

    const invHeader = (inv: Invoice) => (
        <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-2">
                <span className="text-sm font-medium truncate">
                    {inv.payer?.documentNumber ? `${formatTaxId(inv.payer.documentNumber)} · ` : ''}{inv.payer?.name || '—'}
                </span>
                {/* Título/NF e vencimento sobem para a linha do VALOR: quem confere um
                    pagamento procura a cobrança pelo documento e pela data, e ler os três juntos
                    é a conferência. Título/NF e NÃO o id da fatura: estas linhas são RASCUNHOS — a
                    fatura ainda não existe, e o `invoiceId` daqui é o da fatura de ORIGEM, que não
                    diz nada sobre o que está sendo cobrado. Sem documento, mostra o rótulo vazio em
                    vez de sumir: a ausência do documento é informação. */}
                <span className="text-sm whitespace-nowrap">
                    <span className="text-xs text-gray-500">
                        Título/NF {inv.documentTitle || '-'} · vence {fmtDate(inv.dueDate)} ·{' '}
                    </span>
                    <span className="font-medium">R$ {fmt(inv.amount)}</span>
                </span>
            </div>
            <div className="flex justify-between gap-2 text-xs text-gray-500">
                <span className="truncate">
                    {/* Do contrato para a fatura, do mais amplo ao mais específico. Contrato-
                        aditamento como nas colunas de Contrato das telas de Operações e Títulos —
                        uuid de operação não diz nada a quem lê.
                        ⚠️ Dizia "Operação" e mostrava o CONTRATO. São dois identificadores, e
                        agora os dois aparecem, cada um com o seu nome. */}
                    Contrato {inv.contractLabel || inv._op || inv.creditOperationId.slice(0, 8)}
                    {inv.operationNumber && <> · Operação {inv.operationNumber}</>}
                    {inv.chargeId ? ` · #${inv.chargeId.slice(0, 8)}` : ''}
                    {/* Ordem padrão das telas de cobrança: natureza, Título/NF, Nº.
                        O número do título anda com rótulo PRÓPRIO ("Nº 2") e não colado na
                        natureza ("Garantia 2") — ali a palavra nomeava o número, e é a natureza. */}
                    {inv.chargeScenario && (
                        <> · {CHARGE_SCENARIO_LABELS[inv.chargeScenario] || inv.chargeScenario}</>
                    )}
                    {inv.installmentNumber != null && <> · Nº {inv.installmentNumber}</>}
                </span>
                {/* valor NOMINAL: sem ele o usuário não vê que o rascunho é o SALDO (nominal menos
                    o que já foi pago) */}
                <span className="whitespace-nowrap">
                    {inv.nominalAmount != null && Number(inv.nominalAmount) !== inv.amount && (
                        <>Valor Nominal R$ {fmt(Number(inv.nominalAmount))}</>
                    )}
                </span>
            </div>
        </div>
    );

    /**
     * Efeito no título. O widget muda com o cenário porque as escolhas mudam:
     *   RECEIVABLE  → checkbox liquida/não liquida (como sempre foi)
     *   INSTALLMENT → select com os três, incl. baldinho (pago pela garantia)
     *   GUARANTEE   → mesmo checkbox do recebível, decidindo o efeito no título de GARANTIA, com
     *                 a nota de que o valor também é distribuído pelas parcelas da operação
     *   desconhecido → travado em total: cenário que esta versão da tela não conhece (API mais
     *                 nova) degrada para o modo mais conservador em vez de adivinhar
     */
    const liquidationControl = (inv: Invoice, e: ItemEdit) => {
        const allowedModes = modesOf(inv);
        const cor = e.mode === 'TOTAL' ? 'bg-green-500' : 'bg-amber-500';

        if (allowedModes.length <= 1) {
            return (
                <div className="mt-5 self-start">
                    <span className="inline-block px-2 py-1 rounded text-sm bg-green-500 text-white">
                        {MODE_LABELS.TOTAL}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                        Cenário não reconhecido por esta tela: só liquidação total.
                    </p>
                </div>
            );
        }
        if (allowedModes.includes('OPERATION_WATERFALL')) {
            return (
                <div className="mt-5 self-start w-full">
                    {/* Sem rótulo VISÍVEL: as opções já estão no imperativo ("Liquidar o Título")
                        e dizem sozinhas o que fazem. O aria-label fica porque tirar o <label>
                        deixaria o select sem nome nenhum para leitor de tela — e a explicação do
                        baldinho vai no title, não numa legenda fixa que empurra a linha e só
                        interessa a quem escolheu essa opção. */}
                    <select id={`modo-${inv.invoiceId}`} aria-label="Efeito no título"
                        className={`${input} ${cor} text-white border-0`}
                        value={e.mode}
                        title={e.mode === 'OPERATION_WATERFALL' ? WATERFALL_HINT : undefined}
                        onChange={ev => patchEdit(inv, { mode: ev.target.value as LiquidationMode })}>
                        {allowedModes.map(m => (
                            <option key={m} value={m}
                                title={m === 'OPERATION_WATERFALL' ? WATERFALL_HINT : undefined}>
                                {MODE_ACTIONS[m]}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }
        // estado destacado nas cores dos botões da tela de títulos:
        // verde = liquida (Liquidar), âmbar = não liquida (Baixa Parcial)
        return (
            <div className="mt-5 self-start">
                <label className={`flex items-center gap-2 text-sm px-2 py-2 rounded cursor-pointer transition-colors self-start ${e.mode === 'TOTAL'
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}>
                    <input type="checkbox" className="accent-white" checked={e.mode === 'TOTAL'}
                        onChange={ev => patchEdit(inv, { mode: ev.target.checked ? 'TOTAL' : 'PARTIAL' })} />
                    Quitação total do título
                </label>
                {inv.chargeScenario === 'GUARANTEE' && (
                    <p className="text-xs text-gray-500 mt-1">
                        Efeito no título de <strong>garantia</strong>. O valor é também distribuído pelas
                        parcelas da operação, nos dois casos.
                    </p>
                )}
            </div>
        );
    };

    const editControls = (inv: Invoice, e: ItemEdit) => (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2" onClick={ev => ev.stopPropagation()}>
            <div className="mb-2 h-[86px]">
                {/* `CurrencyInput` do sistema: o estado da linha continua guardando BRL em TEXTO
                    (é o que `parseBRL`/`fmt` e o payload usam), então a ponte é feita aqui — número
                    para o campo, texto formatado de volta para o rascunho. */}
                <CurrencyInput
                    id={`valor-pago-${inv.invoiceId}`}
                    label="Valor pago (R$)"
                    prefix="R$"
                    fractionDigits={2}
                    value={parseBRL(e.value)}
                    onChange={num => patchEdit(inv, { value: fmt(num) })}
                    className="mt-4 mb-0"
                />
                {/* desconto/acréscimo só quando quita o título (em pagamento parcial o valor
                    menor é expected, não é desconto) */}
                <HintBadge hint={e.mode === 'TOTAL' ? amountHint(parseBRL(e.value), invoiceExpected(inv)) : null} />
            </div>
            {liquidationControl(inv, e)}
        </div>
    );

    /**
     * As escolhas de COBRANÇA da linha, em três colunas.
     *
     * O efeito é sobre TODAS as faturas abertas daquele charge, não só a original: pagar a dívida
     * do título torna as irmãs de uma quebra em frações cobrança de valor já pago. O escopo por
     * charge é resolvido no backend (ProcessPayment._apply_flags).
     */
    const chargeControls = (inv: Invoice, e: ItemEdit) => {
        const refundAllowed = allowsGuarantorRefund(sources, e.payment_source);
        return (
            <div className="grid grid-cols-3 gap-x-6 gap-y-2 mt-2" onClick={ev => ev.stopPropagation()}>
                <div>
                    {/* o vazio NÃO é uma resposta válida: ele marca que falta decidir, e o envio
                        fica bloqueado até que se decida (ver `canSubmit`) */}
                    <SelectTextField
                        id={`demais-${inv.invoiceId}`}
                        label="Demais faturas abertas:"
                        value={e.promise}
                        emptyValue=""
                        items={OPEN_INVOICES_ITEMS}
                        errorMessage={missingPromise(e) ? 'Escolha um destino para concluir' : undefined}
                        onChange={ev => patchEdit(inv,
                            { promise: ev.target.value as OpenInvoicesChoice })}
                    />
                </div>
                {/* fonte e aval só no ato desmembrado: eles viajam na MARCAÇÃO, e o payload do ato
                    único não tem onde levá-los — ver o estado `sources` */}
                {identifying && (
                    <>
                        <div>
                            {/* sem opção vazia: o `default_if` do seed cobre as três naturezas,
                                então sempre há uma fonte pré-selecionada */}
                            <SelectTextField
                                id={`fonte-${inv.invoiceId}`}
                                label="Fonte de Pagamento:"
                                hideEmptyOption
                                value={e.payment_source}
                                items={sources.map(src => ({ value: src.alias, label: src.name }))}
                                onChange={ev => patchEdit(inv,
                                    { payment_source: ev.target.value })}
                            />
                        </div>
                        <div className="flex items-center">
                            <label className={`inline-flex items-center gap-2 text-sm ${refundAllowed ? '' : 'opacity-50 cursor-not-allowed'}`}
                                title={refundAllowed ? undefined
                                    : 'Só a fonte de pagamento que admite devolução habilita este campo'}>
                                <input type="checkbox" checked={e.pending_guarantor_refund}
                                    disabled={!refundAllowed}
                                    onChange={ev => patchEdit(inv,
                                        { pending_guarantor_refund: ev.target.checked })} />
                                Aval a Devolver
                            </label>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <PageForm
            pageTitle={PAGE_TITLE[mode]}
            isLoading={submitting}
            breadcrumb={[
                { label: 'Home', url: '/home' },
                { label: 'Financeiro', url: '#' },
                { label: PAGE_TITLE[mode], url: PAGE_ROUTE[mode] },
            ]}
            apiVersion=""
            /* O stepper é o controle desta tela: fica no slot do template, grudado no cabeçalho,
               em vez de rolar junto com o formulário. */
            controlSlot={<Stepper step={step} steps={STEPS} />}
        >
            <div className="py-4 max-w-6xl mx-auto">

                {/* ═══ STEP 1 — IDENTIFY: escolher um pagamento que já entrou ═══ */}
                {step === 0 && identifying && (
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
                        <h2 className="font-semibold text-gray-800 dark:text-white mb-1">
                            Selecionar Pagamento
                        </h2>
                        <p className="text-xs text-gray-500 mb-4">
                            O dinheiro já entrou — registrado pelo financeiro na tela de Pagamentos.
                            Aqui você diz <strong>quais faturas ele paga</strong>. As faturas ficam
                            sinalizadas e só são liquidadas quando o financeiro aprovar a identificação.
                        </p>

                        <PendingPaymentPicker
                            selectedId={selectedPayment?.paymentId}
                            onSelect={setSelectedPayment}
                            onInfo={setInfoPayment}
                        />

                        {selectedPayment && (
                            <div className="mt-4 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                                A conta a fechar nas próximas etapas é{' '}
                                <strong>R$ {fmt(paymentPending)}</strong>
                                {partiallyIdentified && (
                                    <> — o que ainda falta identificar deste pagamento, de um total de{' '}
                                        R$ {fmt(paymentAmount)}</>
                                )}
                                {selectedPayment.payer?.name && <> · {selectedPayment.payer.name}</>}
                            </div>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button disabled={!step1Valid} onClick={() => setStep(1)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">Avançar →</button>
                        </div>
                    </div>
                )}

                {/* ═══ STEP 1 — REGISTER (deprecated): informar o dinheiro na mão ═══ */}
                {step === 0 && !identifying && (
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
                        <h2 className="font-semibold text-gray-800 dark:text-white mb-4">Dados do Recebimento</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <PaymentDateInput label="Data do Pagamento *" date={date} time={time} showTime={showTime}
                                    onChange={next => { setDate(next.date); setTime(next.time); setShowTime(next.showTime); }} />
                            </div>
                            <div>
                                <label className={label}>Valor Recebido (R$) *</label>
                                <input type="text" value={amount} onChange={e => setAmount(e.target.value)}
                                    placeholder="2.500.000,00" className={`${input} text-lg font-semibold`} />
                            </div>
                        </div>

                        {/* Tipo de pagamento + campos dependentes (mesmo bloco da baixa de título) */}
                        <div className="mt-4">
                            <PaymentTypeFields
                                value={typeFields}
                                onChange={next => {
                                    // dação: o valor recebido acompanha o total dos bens
                                    const total = assetDationTotal(next.asset_dation);
                                    if (next.payment_type === PaymentType.ASSET_DATION
                                        && total !== assetDationTotal(typeFields.asset_dation)) {
                                        setAmount(total.toFixed(2).replace('.', ','));
                                    }
                                    setTypeFields(next);
                                }}
                                methods={[PaymentMethod.PIX, PaymentMethod.TRANSFER, PaymentMethod.BOLETO]}
                                labelClassName={label}
                                inputClassName={input}
                                idPrefix="register-payment"
                                // fonte e aval saem daqui: quem os informa é a MARCAÇÃO
                                chargeAttributes={false}
                            />
                        </div>

                        {/* Pagador (bloco compartilhado com a baixa de título) */}
                        <div className="mt-6 border-t dark:border-slate-700 pt-4">
                            <PayerFields value={payer} onChange={setPayer}
                                labelClassName={label} inputClassName={input} />
                        </div>

                        <div className="mt-6 border-t dark:border-slate-700 pt-4">
                            <label className={label}>Comprovante de pagamento (opcional)</label>
                            <label className="flex items-center gap-2 border border-dashed dark:border-slate-600 rounded px-3 py-3 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
                                <FaCloudUploadAlt /> {receiptFile?.name || 'Selecionar arquivo PDF'}
                                <input type="file" accept="application/pdf" className="hidden"
                                    onChange={e => setReceiptFile(e.target.files?.[0] || null)} />
                            </label>
                        </div>

                        <div className="mt-4">
                            <label className={label}>Observações</label>
                            <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                                placeholder="Referência bancária, NF, etc." className={input} />
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button disabled={!step1Valid} onClick={() => setStep(1)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">Avançar →</button>
                        </div>
                    </div>
                )}

                {/* ═══ STEP 2 ═══ */}
                {step === 1 && (
                    <>
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="font-semibold text-gray-800 dark:text-white">Buscar Operações de Crédito</h2>
                            <div className="text-sm font-semibold text-green-700">
                                {partiallyIdentified
                                    ? <>Em aberto: R$ {fmt(paymentPending)}{' '}
                                        <span className="font-normal text-gray-500">
                                            (de R$ {fmt(paymentAmount)})
                                        </span></>
                                    : <>Valor: R$ {fmt(paymentAmount)}</>}
                            </div>
                        </div>

                        <FilterBuilder
                            schema={operationsFilterSchema}
                            onSearch={runSearch}
                            loading={searching}
                            title="Filtros de operações"
                        />

                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                            {selectedOps.length > 0 && (
                                <div className="px-4 py-3 bg-blue-50 dark:bg-slate-700/40 border-b dark:border-slate-700">
                                    <span className="text-xs font-semibold text-blue-700 uppercase">Selecionados:</span>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {selectedOps.map(o => (
                                            <span key={o.credit_operation_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-slate-600 rounded text-sm">
                                                {opLabel(o)} <span className="text-gray-400 text-xs">{o.operation_number}</span>
                                                <button onClick={() => toggleOp(o)} className="text-gray-400 hover:text-red-500 ml-1"><FaTimes size={11} /></button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {results.length > 0 ? (
                                <>
                                    <div className="divide-y dark:divide-slate-700 max-h-[50vh] overflow-y-auto">
                                        {results.map(op => {
                                            return (
                                                <div key={op.credit_operation_id} className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer"
                                                    onClick={() => toggleOp(op)}>
                                                    <span className="mr-3">{isOpSelected(op) ? <FaCheckSquare className="text-blue-600 text-lg" /> : <FaRegSquare className="text-lg text-gray-400" />}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-baseline gap-2">
                                                            <span className="font-medium">{opLabel(op)}</span>
                                                            <span className="text-xs text-gray-500 truncate">{op.operation_number}</span>
                                                            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded whitespace-nowrap">{op.operation_type}</span>
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate">
                                                            {op.issuer_document ? `${formatTaxId(op.issuer_document)} · ` : ''}{op.issuer_name || '—'}
                                                            {op.credit_product_type_name ? ` · ${op.credit_product_type_name}` : ''}
                                                        </div>
                                                    </div>
                                                    <div className="text-right whitespace-nowrap ml-2">
                                                        {/* `amount` = soma dos títulos: é o que a
                                                        operação de fato deve, e é com ele que o
                                                        recebimento é conciliado. NÃO o
                                                        nominal_value, que vem do credit e não
                                                        responde por isso. Sem título ainda, não
                                                        há o que conciliar — mostra "—" em vez de
                                                        cair num número de outra fonte. */}
                                                        <div className="text-sm">
                                                            {op.amount != null ? `R$ ${fmt(Number(op.amount))}` : '—'}
                                                        </div>
                                                        {op.amount != null && (op.paid_amount ?? 0) > 0 && (
                                                            <div className="text-xs text-green-600">recebido R$ {fmt(Number(op.paid_amount))}</div>
                                                        )}
                                                        {op.expiration_date && (
                                                            <div className="text-xs text-gray-500">vence {op.expiration_date.slice(0, 10).split('-').reverse().join('/')}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {opTotal > OP_PAGE_SIZE && (
                                        <div className="flex items-center justify-between px-4 py-2 border-t dark:border-slate-700 text-xs text-gray-500">
                                            <span>{opTotal} operação(ões) · página {opPage + 1} de {Math.ceil(opTotal / OP_PAGE_SIZE)}</span>
                                            <div className="flex gap-2">
                                                <button disabled={opPage === 0 || searching} onClick={() => changeOpPage(opPage - 1)}
                                                    className="px-2 py-1 border rounded disabled:opacity-40 dark:border-slate-600">← Anterior</button>
                                                <button disabled={(opPage + 1) * OP_PAGE_SIZE >= opTotal || searching} onClick={() => changeOpPage(opPage + 1)}
                                                    className="px-2 py-1 border rounded disabled:opacity-40 dark:border-slate-600">Próxima →</button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="p-8 text-center text-gray-400">
                                    {searching ? 'Buscando...' : opSearched ? 'Nenhuma operação encontrada com esses filtros' : 'Use os filtros acima para buscar operações'}
                                </div>
                            )}

                            <div className="p-4 border-t dark:border-slate-700 flex items-center justify-between bg-gray-50 dark:bg-slate-800">
                                <div className="flex items-center gap-4">
                                    {/* só com resultado na tela: uma caixa que não seleciona nada
                                    convida a clicar e não fazer efeito */}
                                    {results.length > 0 && (
                                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                                            <input type="checkbox" checked={allOnPageSelected}
                                                /* parcial: nem marcada nem vazia. Sem isto, com metade
                                                   escolhida a caixa aparece DESMARCADA e sugere que
                                                   nada foi selecionado. */
                                                ref={el => {
                                                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                                                }}
                                                onChange={ev => toggleAllOnPage(ev.target.checked)} />
                                            Selecionar Tudo
                                        </label>
                                    )}
                                    <span className="text-sm text-gray-600 dark:text-gray-300">{selectedOps.length} operação(ões) selecionada(s)</span>
                                </div>
                                <div className="flex gap-3">
                                    <button onClick={() => setStep(0)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                                    <button disabled={selectedOps.length === 0 || loadingInvoices} onClick={goToInvoices}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                                        {loadingInvoices ? 'Carregando...' : 'Avançar →'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ═══ STEP 3 ═══ */}
                {step === 2 && (
                    <div className="space-y-4">
                        {/* Barra de alocação (sempre visível) */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                            <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-gray-600 dark:text-gray-300">Alocado: <b className={over ? 'text-red-600' : 'text-green-700'}>R$ {fmt(committed)}</b> / R$ {fmt(paymentAmount)}</span>
                                <div className="flex items-center gap-3">
                                    <span className={`font-semibold ${over ? 'text-red-600' : complete ? 'text-green-600' : 'text-gray-500'}`}>
                                        {over ? `Excede R$ ${fmt(committed - paymentAmount)}` : complete ? '✓ 100%' : `Falta R$ ${fmt(paymentAmount - committed)}`}
                                    </span>
                                    {/* os dois agem sobre a SELEÇÃO, e é aqui que ela está resumida —
                                    dentro do accordion eles rolariam para fora da vista */}
                                    <button onClick={() => setBulkOpen(true)}
                                        disabled={selectedInvoices.length === 0}
                                        className="px-3 py-1 text-xs border rounded dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed">
                                        Ações em Lote
                                    </button>
                                    <button onClick={() => setEdits({})}
                                        disabled={selectedInvoices.length === 0}
                                        className="px-3 py-1 text-xs border rounded dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed">
                                        Limpar Seleção
                                    </button>
                                </div>
                            </div>
                            {/* Quatro fatias sobre o valor TOTAL do pagamento, na ordem do que já é
                            fato para o que ainda é rascunho: efetivado (verde), marcado antes
                            (âmbar), alocado agora (azul), a marcar (o fundo cinza da própria
                            barra).

                            ⚠️ Em 100% a barra NÃO vira um bloco verde: as fatias continuam
                            visíveis, e quem diz que fechou é o "✓ 100%" ao lado. Pintar tudo de
                            verde ali afirmaria que o pagamento está identificado quando ele
                            apenas terminou de ser SINALIZADO. Acima de 100% ela fica toda
                            vermelha — é o único estado que impede o envio. */}
                            <div className="w-full h-3 bg-gray-200 dark:bg-slate-700 rounded overflow-hidden flex">
                                {over ? (
                                    <div className="h-full w-full bg-red-500" />
                                ) : (
                                    <>
                                        <div className="h-full bg-green-500 transition-all"
                                            title={`Já efetivado: R$ ${fmt(settledAmount)}`}
                                            style={{ width: `${slices.settled}%` }} />
                                        <div className="h-full bg-amber-400 transition-all"
                                            title={`Marcado anteriormente: R$ ${fmt(markedAmount)}`}
                                            style={{ width: `${slices.marked}%` }} />
                                        <div className="h-full bg-blue-500 transition-all"
                                            title={`Alocado agora: R$ ${fmt(allocated)}`}
                                            style={{ width: `${slices.now}%` }} />
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ── Accordion 0: Já marcado neste pagamento ──
                        Só aparece quando há algo: uma seção vazia em todo pagamento novo seria
                        ruído permanente para um caso que é a exceção.

                        ⚠️ `released.length` entra na condição. Sem ele, remover a ÚLTIMA marcação
                        escondia a seção inteira — e junto o aviso de que a remoção ainda não foi
                        gravada. O usuário ficava com uma remoção pendente e nenhuma pista de que
                        ela existia. */}
                        {identifying && (settledItems.length > 0 || markedItems.length > 0
                            || released.length > 0) && (
                                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                                    <button onClick={() => setOpenMarked(v => !v)}
                                        className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                                        <span>
                                            Já marcado(s) previamente neste pagamento: {settledItems.length + markedItems.length}
                                            {settledItems.length > 0 && (
                                                <span className="ml-2 text-xs font-normal text-gray-600 dark:text-gray-300">
                                                    ({settledItems.length} já efetivada(s))
                                                </span>
                                            )}
                                        </span>
                                        {openMarked ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                                    </button>
                                    {openMarked && (
                                        <div className="border-t dark:border-slate-700 max-h-[35vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-2">
                                            {/* EFETIVADAS primeiro, e sem botão nenhum: é o que já
                                        aconteceu. O verde é o mesmo da barra — quem olha as duas
                                        coisas está lendo a mesma informação. */}
                                            {settledItems.map(item => (
                                                <div key={item.invoiceId}
                                                    className="flex items-center gap-3 rounded-md border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 px-4 py-2.5">
                                                    {allocationHeader(item)}
                                                    {/* nem editar, nem remover: o que foi aprovado saiu da
                                                sinalização e o settlement já movimentou o título */}
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-200 text-green-900 whitespace-nowrap">
                                                        Identificada
                                                    </span>
                                                </div>
                                            ))}

                                            {markedItems.map(item => (
                                                <div key={item.invoiceId}
                                                    className="flex items-center gap-3 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 px-4 py-2.5">
                                                    {allocationHeader(item)}
                                                    <button onClick={() => editMarking(item)}
                                                        aria-label={`Editar marcação de ${item.contractLabel || item.invoiceId}`}
                                                        title="Editar: leva para as faturas selecionadas com os dados salvos"
                                                        className="p-1.5 rounded text-blue-600 hover:bg-blue-100 dark:hover:bg-slate-700">
                                                        <FaPen size={13} />
                                                    </button>
                                                    <button onClick={() => removeMarking(item)}
                                                        aria-label={`Remover marcação de ${item.contractLabel || item.invoiceId}`}
                                                        title="Remover: o título volta a ficar disponível para seleção"
                                                        className="p-1.5 rounded text-red-600 hover:bg-red-100 dark:hover:bg-slate-700">
                                                        <FaTimes size={13} />
                                                    </button>
                                                </div>
                                            ))}

                                            {/* ⚠️ A promessa que sustenta a seção inteira: sair sem enviar
                                        não perde nada, e nada foi gravado ainda. */}
                                            {released.length > 0 && (
                                                <p className="text-xs text-gray-500 px-1 pt-1">
                                                    {`${released.length === 1
                                                        ? '1 marcação será removida'
                                                        : `${released.length} marcações serão removidas`}`
                                                        + ' ao concluir. Nada foi gravado ainda — sair desta'
                                                        + ' tela desfaz a remoção.'}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                        {/* ── Accordion A: Faturas selecionadas (editar + submit) ── */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                            <button onClick={() => setOpenSel(v => !v)}
                                className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                                {/* o contador de pendências vive num span PRÓPRIO: colado no
                                    título, ele mudaria o texto que o resto da tela (e os testes)
                                    identificam como "Faturas selecionadas: N" */}
                                <span className="flex items-center gap-2">
                                    <span>Faturas selecionadas: {selectedInvoices.length}</span>
                                    {rowsIncomplete > 0 && (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-100">
                                            {rowsIncomplete} a completar
                                        </span>
                                    )}
                                </span>
                                {openSel ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                            </button>
                            {openSel && (
                                <div className="border-t dark:border-slate-700 max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-2">
                                    {selectedInvoices.length === 0 && (
                                        <div className="p-4 text-center text-gray-400 text-sm">Nenhuma fatura selecionada — selecione na área abaixo</div>
                                    )}
                                    {selectedInvoices.map(inv => {
                                        const e = edits[inv.invoiceId];
                                        // borda âmbar no card que ainda não está completo: com a
                                        // lista rolando, é o que leva o olho até ele
                                        const incomplete = !!e && rowIncomplete(e);
                                        return (
                                            <div key={inv.invoiceId}
                                                className={`bg-[#f0f8ff] dark:bg-slate-700/40 rounded-md border px-4 py-3 ${incomplete
                                                    ? 'border-amber-400 dark:border-amber-500'
                                                    : 'border-blue-200 dark:border-slate-700'}`}>
                                                <div className="flex items-start gap-3">
                                                    {invHeader(inv)}
                                                    <button onClick={() => toggleInvoice(inv)} title="Remover da seleção"
                                                        className="text-gray-400 hover:text-red-500 mt-0.5"><FaTimes /></button>
                                                </div>
                                                {e && editControls(inv, e)}
                                                {e && chargeControls(inv, e)}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ── Accordion B: Operações / Faturas (listagem + seleção) ── */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                            <button onClick={() => setOpenList(v => !v)}
                                className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                                <span>Operações: {selectedOps.length} · Faturas: {invoices.length}</span>
                                {openList ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                            </button>
                            {openList && (
                                <div className="border-t dark:border-slate-700">
                                    <div className="p-3 border-b dark:border-slate-700 flex flex-col sm:flex-row gap-3 sm:items-start">
                                        <div className="flex-1">
                                            <TextEdit
                                                id="filtro-faturas"
                                                label="Filtrar por pagador, recebível, contrato..."
                                                value={invSearch}
                                                onChange={e => setInvSearch(e.target.value)}
                                                placeholder=" "
                                                className="mb-0"
                                            />
                                        </div>
                                        <div className="sm:w-72">
                                            <SelectTextField
                                                id="ordenar-faturas"
                                                label="Ordenar"
                                                hideEmptyOption
                                                value={invSort}
                                                items={INV_SORT_ITEMS}
                                                onChange={e => setInvSort(e.target.value as any)}
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-3">
                                        {groups.length === 0 && <div className="p-8 text-center text-gray-400">Nenhum título com dívida em aberto</div>}
                                        {groups.map(({ op, list }) => {
                                            return (
                                                <div key={op.credit_operation_id} className="rounded-md border border-gray-300 dark:border-slate-700 overflow-hidden">
                                                    <div className="px-4 py-2 bg-gray-200 dark:bg-slate-700/50 text-sm font-medium flex flex-wrap gap-x-3 gap-y-1">
                                                        {/* valor e pago vêm da própria busca agora: a
                                                        lista é do settlement e já traz os dois.
                                                        Antes a tela fazia uma chamada por operação
                                                        só para buscá-los. */}
                                                        <span>{opLabel(op)}</span>
                                                        {/* EMITENTE / CEDENTE da operação — já vem
                                                        eleito da vw_operations_list, o mesmo
                                                        participante que as telas de Títulos e de
                                                        Faturas mostram. Aqui ele responde "de quem
                                                        é esta dívida?" sem obrigar a abrir outra
                                                        tela no meio da identificação.

                                                        ⚠️ `truncate` + teto de 500px SÓ neste
                                                        item: razão social de agro é longa e, num
                                                        cabeçalho `flex-wrap`, ela empurra Valor,
                                                        Pago e Faturas para a linha de baixo — o
                                                        cabeçalho dobra de altura em cada bloco. O
                                                        nome inteiro fica no `title`. */}
                                                        {op.issuer_name && (
                                                            <span className="text-gray-500 dark:text-gray-400 font-normal truncate max-w-[500px]"
                                                                title={op.issuer_name}>
                                                                | {op.issuer_document ? `${formatTaxId(op.issuer_document)} · ` : ''}{op.issuer_name}
                                                            </span>
                                                        )}
                                                        {op.amount != null && <span className="text-gray-500 dark:text-gray-400 font-normal">| Valor: R$ {fmt(Number(op.amount))}</span>}
                                                        {op.amount != null && <span className="text-gray-500 dark:text-gray-400 font-normal">| Pago: R$ {fmt(Number(op.paid_amount || 0))}</span>}
                                                        <span className="text-gray-500 dark:text-gray-400 font-normal">| Faturas: {list.length}</span>
                                                    </div>
                                                    <div className="p-2 space-y-1.5 bg-gray-100/70 dark:bg-slate-800/40">
                                                        {list.map(inv => {
                                                            const sel = isSelected(inv);
                                                            /* Título que já tem marcação pendente de
                                                               OUTRO pagamento. Um título só aceita uma
                                                               marcação: sem isso a primeira aprovação
                                                               pagaria a fatura e a segunda acharia
                                                               fatura já paga apontando para outro
                                                               pagamento — o mesmo dinheiro duas vezes.
                                                               O servidor recusa; aqui a linha entra
                                                               desabilitada para o usuário não montar o
                                                               lote todo e perder o trabalho no fim. */
                                                            /* Três situações, e a linha as distingue:
                                                               marcada NESTE pagamento (bloqueada, com
                                                               tag própria — mexer nela é pela seção "Já
                                                               marcado(s)"), marcada em OUTRO (bloqueada
                                                               pelo motivo de sempre), ou livre.
    
                                                               ⚠️ Removida em "Já marcado(s)" nesta
                                                               sessão, ela VOLTA a ficar livre: é para
                                                               isso que o usuário a removeu. Por isso a
                                                               checagem olha `markedItems`, que já
                                                               desconta o que foi liberado, e não as
                                                               alocações cruas. */
                                                            const markedHere = markedItems.some(
                                                                a => sameCharge(inv, a));
                                                            const releasedHere = allocations.some(
                                                                a => sameCharge(inv, a)
                                                                    && released.includes(a.invoiceId));
                                                            const chargeTaken = markedHere
                                                                || (!!inv.hasSignalledPayment && !releasedHere);
                                                            return (
                                                                <div key={inv.invoiceId}
                                                                    title={markedHere
                                                                        ? 'Este título já está marcado NESTE pagamento — edite ou remova pela seção "Já marcado(s) previamente neste pagamento", acima.'
                                                                        : chargeTaken
                                                                            ? 'Este título já está marcado para outro pagamento. Finalize ou remova a sinalização daquele pagamento antes.'
                                                                            : undefined}
                                                                    aria-disabled={chargeTaken || undefined}
                                                                    className={`flex items-center gap-3 px-3 py-2 rounded border ${chargeTaken
                                                                        ? 'bg-gray-100 dark:bg-slate-900/60 border-gray-200 dark:border-slate-700 opacity-60 cursor-not-allowed'
                                                                        : sel ? 'border-blue-300 dark:border-blue-500 bg-[#f0f8ff] dark:bg-slate-700/50 cursor-pointer'
                                                                            : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/30 cursor-pointer'}`}
                                                                    onClick={() => { if (!chargeTaken) toggleInvoice(inv); }}>
                                                                    <span>{sel ? <FaCheckSquare className="text-blue-600" /> : <FaRegSquare className="text-gray-300" />}</span>
                                                                    {invHeader(inv)}
                                                                    {chargeTaken && (
                                                                        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap">
                                                                            {markedHere ? 'Marcado neste pagamento' : 'Pagto Sinalizado'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer — os dois botões JUNTOS à direita, como no passo 2: voltar e
                        concluir são o mesmo par de decisões, e separá-los pelas pontas da tela
                        obrigava a atravessá-la para trocar de ideia. */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                            {/* O porquê de não dar para concluir, VISÍVEL. Antes morava só no
                                `title` do botão — quem não passava o mouse via um botão apagado
                                sem explicação e não tinha como descobrir o que faltava. */}
                            {!submitting && pendingReasons.length > 0 && (
                                <div role="status" aria-live="polite"
                                    className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-500/10">
                                    <p className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-100">
                                        <FaExclamationTriangle size={13} />
                                        Para concluir, ainda falta:
                                    </p>
                                    <ul className="mt-1 list-disc space-y-0.5 pl-6 text-sm text-amber-900 dark:text-amber-100">
                                        {pendingReasons.map(reason => <li key={reason}>{reason}</li>)}
                                    </ul>
                                </div>
                            )}
                            <div className="flex items-center justify-end gap-3">
                                <button onClick={() => setStep(1)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                                <button disabled={!canSubmit || submitting} onClick={submit}
                                    title={canSubmit ? '' : pendingReasons.join(' ')}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 hover:bg-green-700">
                                    {submitting ? 'Registrando...'
                                        : identifying ? 'Confirmar' : 'Confirmar Pagamento'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {infoPayment && (
                <PaymentInfoModal payment={infoPayment} onClose={() => setInfoPayment(null)} />
            )}

            {bulkOpen && (
                <BulkInvoiceActionsModal
                    selectedCount={selectedInvoices.length}
                    sources={sources}
                    onApply={applyBulk}
                    onClose={() => setBulkOpen(false)}
                />
            )}
        </PageForm>
    );
};

export default RegisterPaymentPage;
